const COLOURS = ['#ff386c', '#ff9726', '#ffe640', '#bd63ff', '#44e98a', '#83e337'];
const fail = () => {
  throw new RangeError('Invalid Orchard Rush action.');
};
function random(state) {
  state.rng = (Math.imul(state.rng, 1664525) + 1013904223) >>> 0;
  return state.rng / 4294967296;
}

function worldSize(world) {
  if (
    !world ||
    !Number.isFinite(world.width) ||
    !Number.isFinite(world.height) ||
    world.width < 160 ||
    world.width > 4096 ||
    world.height < 160 ||
    world.height > 2048
  )
    fail();
  return { width: world.width, height: world.height };
}

/** Reproject a private round without spending time or restarting its seeded wave.
 * Coordinates and velocity use world units and units/ms; circles retain their
 * radius so one uniform viewport scale keeps artwork and hit geometry identical. */
export function resizeOrchardRush(input, world) {
  const next = worldSize(world);
  if (input.world.width === next.width && input.world.height === next.height) return input;
  const sx = next.width / input.world.width,
    sy = next.height / input.world.height;
  return {
    ...input,
    world: next,
    events: [],
    fruits: input.fruits.map((f) => ({
      ...f,
      x: f.x * sx,
      y: f.y * sy,
      vx: f.vx * sx,
      vy: f.vy * sy,
    })),
  };
}

/** Private one-minute arcade physics, in viewport world units and milliseconds.
 * Callers inject bounded active time; hidden time never becomes progress. Scores
 * are local entertainment, not server-ranked records or evidence of human play. */
export function createOrchardRush(seed = 7, level = 1, world = { width: 360, height: 540 }) {
  if (
    !Number.isInteger(seed) ||
    seed < 0 ||
    seed > 0xffffffff ||
    !Number.isInteger(level) ||
    level < 1 ||
    level > 20
  )
    fail();
  return {
    version: 'orchard-rush-1',
    world: worldSize(world),
    seed,
    rng: seed,
    level,
    elapsed: 0,
    nextWave: 100,
    serial: 0,
    wave: 0,
    fruits: [],
    score: 0,
    sliced: 0,
    missed: 0,
    bestCombo: 0,
    stroke: 0,
    strokeCount: 0,
    comboAwarded: 0,
    completed: false,
    events: [],
  };
}

function spawn(state) {
  state.wave++;
  const sx = state.world.width / 360,
    sy = state.world.height / 540;
  const count = Math.min(6, 2 + Math.floor(state.wave / 5) + Math.floor(state.level / 3));
  const centre = 75 + random(state) * 210;
  for (let i = 0; i < count; i++) {
    const kind = Math.floor(random(state) * 6);
    const thorn = state.wave > 4 && i === count - 1 && state.wave % 4 === 0;
    const x = Math.max(35, Math.min(325, centre + (i - (count - 1) / 2) * 48));
    state.fruits.push({
      id: ++state.serial,
      kind,
      colour: COLOURS[kind],
      thorn,
      x: x * sx,
      y: (584 + i * 9) * sy,
      vx: ((180 - x) * 0.001 + (random(state) - 0.5) * 0.045) * sx,
      vy: (-0.82 - random(state) * 0.1) * sy,
      radius: thorn ? 23 : 28 + (kind % 3),
      rotation: (random(state) - 0.5) * 1.2,
      spin: (random(state) - 0.5) * 0.002,
    });
  }
  state.nextWave += Math.max(1150, 1900 - state.level * 50 - state.wave * 9);
}

/** Advance accepted physics by0..50ms; the bounded array and fixed steps prevent
 * tab-resume leaps. A completed round is held until an explicit new round. */
export function stepOrchardRush(input, milliseconds) {
  if (!Number.isFinite(milliseconds) || milliseconds < 0 || milliseconds > 50) fail();
  if (input.completed) return input;
  const state = structuredClone(input);
  const dt = Math.min(milliseconds, 60000 - state.elapsed);
  state.events = [];
  state.elapsed += dt;
  if (state.elapsed >= state.nextWave && state.elapsed < 58000 && state.fruits.length < 12)
    spawn(state);
  for (const fruit of state.fruits) {
    fruit.x += fruit.vx * dt;
    fruit.y += fruit.vy * dt + 0.00046 * (state.world.height / 540) * dt * dt;
    fruit.vy += 0.00092 * (state.world.height / 540) * dt;
    fruit.rotation += fruit.spin * dt;
  }
  state.fruits = state.fruits.filter((fruit) => {
    const keep = fruit.y < (630 / 540) * state.world.height || fruit.vy < 0;
    if (!keep && !fruit.thorn) state.missed++;
    return keep;
  });
  if (state.elapsed >= 60000) {
    state.completed = true;
    state.events.push({ type: 'complete', points: state.score });
  }
  return state;
}

/** Squared segment hit geometry catches fast swipes between sampled points. */
export function orchardSegmentHits(fruit, from, to) {
  const dx = to.x - from.x,
    dy = to.y - from.y;
  const length = dx * dx + dy * dy;
  const t = length
    ? Math.max(0, Math.min(1, ((fruit.x - from.x) * dx + (fruit.y - from.y) * dy) / length))
    : 0;
  return (fruit.x - from.x - t * dx) ** 2 + (fruit.y - from.y - t * dy) ** 2 <= fruit.radius ** 2;
}

/** Slice only fruit intersecting a real segment; each fruit pays once. One
 * pointer stroke owns its combo, so repeated pointermove samples cannot farm it. */
export function sliceOrchardRush(input, from, to, stroke) {
  for (const point of [from, to])
    if (
      !point ||
      !Number.isFinite(point.x) ||
      !Number.isFinite(point.y) ||
      point.x < -100 ||
      point.x > input.world.width + 100 ||
      point.y < -100 ||
      point.y > input.world.height + 100
    )
      fail();
  if (!Number.isSafeInteger(stroke) || stroke < 1 || stroke < input.stroke) fail();
  if (input.completed) return input;
  const dx = to.x - from.x,
    dy = to.y - from.y;
  const hits = input.fruits
    .filter((fruit) => orchardSegmentHits(fruit, from, to))
    .sort(
      (a, b) =>
        (a.x - from.x) * dx + (a.y - from.y) * dy - ((b.x - from.x) * dx + (b.y - from.y) * dy) ||
        a.id - b.id,
    );
  if (!hits.length) return input;
  const state = structuredClone(input);
  state.events = [];
  if (state.stroke !== stroke) {
    state.stroke = stroke;
    state.strokeCount = 0;
    state.comboAwarded = 0;
  }
  const ids = new Set(hits.map((fruit) => fruit.id));
  state.fruits = state.fruits.filter((fruit) => !ids.has(fruit.id));
  let comboPoints = 0,
    comboCount = 0;
  for (const fruit of hits) {
    if (fruit.thorn) {
      const loss = Math.min(20, state.score);
      state.score -= loss;
      state.strokeCount = 0;
      state.comboAwarded = 0;
      state.events.push({ ...fruit, type: 'thorn', points: -loss });
    } else {
      state.sliced++;
      state.strokeCount++;
      state.score += 10;
      state.events.push({ ...fruit, type: 'slice', points: 10 });
      if (state.strokeCount >= 3) {
        const earned = state.strokeCount * 15;
        const extra = earned - state.comboAwarded;
        state.comboAwarded = earned;
        state.score += extra;
        comboPoints += extra;
        comboCount = Math.max(comboCount, state.strokeCount);
        state.bestCombo = Math.max(state.bestCombo, state.strokeCount);
      }
    }
  }
  if (comboPoints)
    state.events.push({ type: 'combo', count: comboCount, points: comboPoints, x: to.x, y: to.y });
  return state;
}
